import { NetworkStatus } from 'apollo-boost';
import { useQuery, useSubscription } from 'react-apollo';
import { RigFragmentFragment } from '../../../__generated__/graphql/types';
import { PobNodesQuery } from '../graphql/quartermaster-operations';
import { RigsQuery, SubRigChanges } from '../graphql/rig';

export interface UseQuartermasterDataOptions {
	customerID: string;
	rigID: string;
}

const PAGE_LOADING_STATUSES = [
	NetworkStatus.loading,
	NetworkStatus.setVariables,
	NetworkStatus.refetch,
];

export function useQuartermasterData(options: UseQuartermasterDataOptions) {
	const rigQueryVariables = {
		customerID: options.customerID,
	};
	const pobQueryVariables = {
		customerID: options.customerID,
		rigID: options.rigID,
	};

	const rigsResult = useQuery(RigsQuery, {
		variables: rigQueryVariables,
		fetchPolicy: 'cache-and-network',
		pollInterval: 30000,
		notifyOnNetworkStatusChange: true,
	});
	const pobNodesResult = useQuery(PobNodesQuery, {
		variables: pobQueryVariables,
		skip: !options.rigID,
		fetchPolicy: 'cache-and-network',
		pollInterval: 30000,
		notifyOnNetworkStatusChange: true,
	});

	useSubscription(SubRigChanges, {
		onSubscriptionData(subscriptionOptions) {
			const currentRigs = rigsResult.data?.GetPobRigs;
			const updatedRig: RigFragmentFragment =
				subscriptionOptions.subscriptionData?.data?.RigUpdated?.rig;
			if (!currentRigs || !updatedRig) return;

			const updatedRigs = currentRigs.map((rigItem: RigFragmentFragment) => {
				if (rigItem._id !== updatedRig._id) return rigItem;

				const mergedRig = { ...rigItem };
				Object.entries(updatedRig).forEach(([key, value]) => {
					if (value !== null && value !== undefined) {
						mergedRig[key] = value;
					}
				});
				return mergedRig;
			});

			subscriptionOptions.client.writeQuery({
				query: RigsQuery,
				variables: rigQueryVariables,
				data: {
					GetPobRigs: updatedRigs,
				},
			});

			if (options.rigID) {
				pobNodesResult.refetch();
			}
		},
		fetchPolicy: 'network-only',
	});

	const rigData = rigsResult.data?.GetPobRigs || [];
	const pobNodeData = pobNodesResult.data?.GetRigPobNodes || [];
	const sidebarLoading =
		(rigsResult.networkStatus === NetworkStatus.loading &&
			!rigsResult.data?.GetPobRigs) ||
		(Boolean(options.rigID) &&
			[NetworkStatus.loading, NetworkStatus.setVariables].includes(
				pobNodesResult.networkStatus
			) &&
			!pobNodesResult.data?.GetRigPobNodes);
	const pageLoading =
		PAGE_LOADING_STATUSES.includes(rigsResult.networkStatus) ||
		PAGE_LOADING_STATUSES.includes(pobNodesResult.networkStatus);

	return {
		rigData,
		pobNodeData,
		sidebarLoading,
		pageLoading,
		refetchPobNodes: () => pobNodesResult.refetch(),
	};
}
